import { createTheme } from '@mui/material/styles'
import type { ThemeOptions } from '@mui/material/styles'

// Refer to Figma for the full Firefly Palette:
// figma.com/file/bPOkpSm7oWIKMZ0nNhwUT6/Firefly-Palette

export const BLUE = {
  50: '#E5F3FD',
  100: '#CCE7FB',
  200: '#99CFF7',
  300: '#66B6F3',
  400: '#339EEF',
  500: '#0086EB',
  600: '#0077DC',
  700: '#0059CD',
  800: '#004ABE',
  900: '#004AAF',
  lighter: '#E5F3FD',
  light: '#99CFF7',
  main: '#0077DC',
}

export const NAVY = {
  50: '#E6E9EC',
  100: '#CDD2D8',
  200: '#9CA5B2',
  300: '#6A798B',
  400: '#394C65',
  500: '#071F3E',
  600: '#021A39',
  700: '#021A39',
  800: '#000D2C',
  900: '#000827',
}

export const YELLOW = {
  50: '#FFFBE7',
  100: '#FFF7D0',
  200: '#FFEFA1',
  300: '#FFE772',
  400: '#FFDF43',
  500: '#FFD714',
  600: '#F5C805',
  700: '#E6B900',
  800: '#DCA000',
  900: '#D29100',
}

export const GREY = {
  A50: '#FAFAFA',
  A100: '#F9F9F9',
  50: '#F3F3F3',
  100: '#DDE1E6',
  200: '#C1C7CD',
  300: '#A2A9B0',
  400: '#878D96',
  500: '#697077',
  600: '#4D5358',
  700: '#343A3F',
  800: '#21272A',
  900: '#121619',
  main: '#878D96',
}

export const BRAND = {
  darker: '#D75701',
  dark: '#E1610B',
  main: '#EA5329', // Firefly Orange
  light: '#FAD4C9',
  lighter: '#fef6f4',
}

/**
 * @knipskip - part of the exported Firefly ramp set; the theme wires it into
 * the palette in-file and the theme unit test asserts that wiring, so it has no
 * separate production importer.
 */
export const BLUE_GREY = {
  lighter: '#F3F3F3', // Input background color
  light: '#4A4A4A', // Inactive Label, Inactive Icon
  main: '#7b8f9a',
  dark: '#094A7A',
  darker: '#1d2d3e',
}

/** @knipskip - see BLUE_GREY: wired into the palette in-file, asserted by the theme test. */
export const BROWN = {
  main: '#613510',
}

/** @knipskip - see BLUE_GREY: wired into the palette in-file, asserted by the theme test. */
export const ICONS = {
  successGreen: '#8CD839',
  errorRed: '#E12F2F',
}

// Categorical event colors for the provider schedule calendar, keyed by the
// visit-type variant slug the scheduling BFF sends. Registered on the theme
// (palette.schedule) so the calendar reads them as tokens; FullCalendar's own
// stylesheet forces the `!important` at the point of use.
interface ScheduleEventColor {
  border: string
  background: string
}
const SCHEDULE_EVENT_COLORS = {
  'new-annual-wellness-visit': { border: '#FFD714', background: '#FFFACF' },
  'medical-established': { border: '#5F67C0', background: '#CBD4FE' },
  'annual-wellness-visit-established': { border: '#DC70D3', background: '#F7D7F4' },
  'new-patient': { border: '#469C5D', background: '#A2D0BB' },
  focused: { border: '#FF6334', background: '#FFCEBF' },
  'episodic-established': { border: '#96CDE2', background: '#E6F3F9' },
  'episodic-not-established': { border: '#F9BB2C', background: '#FCDD95' },
  'visit-shift': { border: '#8092E7', background: '#D8E7FF' },
  'out-of-office': { border: '#898D8B', background: '#E6E8E7' },
} as const satisfies Record<string, ScheduleEventColor>
export type ScheduleEventColors = typeof SCHEDULE_EVENT_COLORS

// Firefly semantic accent colors, exported so components can reference them
// directly (e.g. for chart/bar fills) without going through `useTheme()`.
export const ORANGE = BRAND.main // Firefly Orange
export const ORANGE_DARK = BRAND.darker
export const GREEN = ICONS.successGreen
export const RED = ICONS.errorRed

// The default MUI theme, used as a base for values (e.g. palette.grey shades)
// that consumers reference without our customizations layered on top.
export const DEFAULT_THEME = createTheme()

/**
 * Semantic tone pairs (surface background + foreground) for status-colored UI —
 * pills, badges, timeline tiles. The theme exposes these five tones so
 * tone-driven components read their colors from tokens.
 */
interface ToneColor {
  bg: string
  fg: string
}

export interface Tones {
  success: ToneColor
  warning: ToneColor
  error: ToneColor
  info: ToneColor
  neutral: ToneColor
}

// Component overrides the theme builds on, which replaces individual entries
// rather than copying the whole object.
const BASE_COMPONENT_OVERRIDES: ThemeOptions['components'] = {
  MuiFilledInput: {
    styleOverrides: {
      root: {
        backgroundColor: '#F3F3F3',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      clickable: {
        padding: 10,
        '&:hover, &:focus': {
          backgroundColor: GREY[200],
        },
        variants: [
          {
            props: { color: 'primary' },
            style: {
              '&:hover, &:focus': {
                backgroundColor: BLUE[100],
              },
            },
          },
        ],
      },
      colorPrimary: {
        backgroundColor: BLUE[50],
        color: BLUE[600],
      },
    },
  },
  MuiSwitch: {
    styleOverrides: {
      colorPrimary: {
        '&.Mui-checked': {
          color: BLUE[500],
        },
        '&.Mui-checked + .MuiSwitch-track': {
          backgroundColor: BLUE[200],
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        '&:hover': {
          color: BLUE[500],
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        borderBottom: `0.1rem solid ${GREY[100]}`,
      },
      scrollButtons: {
        '&.Mui-disabled': {
          display: 'none',
        },
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: '1.4rem',
      },
    },
  },
}

// The CRM theme — the Member Detail facelift design system
// (docs/projects/2026/t2/crm-member-detail-facelift/architecture.md).
// Provided unconditionally by ThemeProvider; builds on the shared tokens/ramps
// above.

const REFRESH_TEXT = {
  primary: '#0C2340',
  secondary: '#5B6775',
}

/**
 * @knipskip - exported so the theme unit test can assert `palette.tones` wires
 * this source-of-truth map; no production importer (the theme reads it in-file).
 */
export const REFRESH_TONES: Tones = {
  success: { bg: '#E4F3EB', fg: '#0F7B4F' },
  warning: { bg: '#FBF0D8', fg: '#8F5D00' },
  error: { bg: '#FBE7E7', fg: '#B83232' },
  info: { bg: '#E8F1FC', fg: '#1263C4' },
  neutral: { bg: '#EEF1F5', fg: '#5B6775' },
}

const FONT_FAMILY_SANS = "'IBM Plex Sans', Roboto, 'Helvetica Neue', Helvetica, Arial, sans-serif"
const FONT_FAMILY_MONO = "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace"
const CARD_SHADOW = '0 1px 2px rgba(16, 32, 64, 0.04)'
const RAISED_SHADOW = '0 1px 2px rgba(16, 32, 64, 0.12)'

const theme = createTheme({
  typography: {
    // Rem scheme: src/index.css sets <html> to 62.5%,
    // so 1rem = 10px and MUI needs htmlFontSize to compute rems correctly.
    htmlFontSize: 10,
    fontFamily: FONT_FAMILY_SANS,
    fontFamilyMono: FONT_FAMILY_MONO,
    fontWeightMedium: 500,
    body1: {
      fontSize: '1.4rem',
    },
    body2: {
      fontSize: '1.3rem',
    },
    button: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  palette: {
    background: {
      default: '#fff',
      highlight: YELLOW[50],
      external: '#f0e8fc', // Highlights external-facing information
      page: '#EEF1F5',
      pane: '#F5F7FA',
      cardFooter: '#FBFCFD',
    },
    // The Firefly ramps carry over unchanged so every existing palette
    // reference keeps resolving under the theme.
    brand: BRAND,
    primary: BLUE,
    secondary: GREY,
    navy: NAVY,
    grey: GREY,
    blueGrey: BLUE_GREY,
    brown: BROWN,
    schedule: SCHEDULE_EVENT_COLORS,
    text: {
      primary: REFRESH_TEXT.primary,
      secondary: REFRESH_TEXT.secondary,
    },
    divider: GREY[100],
    icons: ICONS,
    tones: REFRESH_TONES,
  },

  components: {
    ...BASE_COMPONENT_OVERRIDES,
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 999,
          fontWeight: 600,
          '&:disabled': {
            color: `rgba(0, 0, 0, 0.4)`,
          },
        },
        contained: {
          boxShadow: RAISED_SHADOW,
          animation: 'none',
          '&:hover': {
            boxShadow: RAISED_SHADOW,
          },
        },
        outlined: {
          variants: [
            {
              props: { color: 'primary' },
              style: {
                borderColor: BLUE[200],
                '&:hover': {
                  borderColor: BLUE[600],
                  backgroundColor: BLUE[50],
                },
              },
            },
          ],
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${GREY[100]}`,
        },
        scrollButtons: {
          '&.Mui-disabled': {
            display: 'none',
          },
        },
      },
    },
  },
})

// Elevation 1 is the design system's resting card shadow and elevation 2 its
// raised/interactive shadow, so `theme.shadows[1]`/`theme.shadows[2]` (what
// Card/Paper and the primitives resolve) are tokens rather than MUI's default
// umbra stacks.
theme.shadows[1] = CARD_SHADOW
theme.shadows[2] = RAISED_SHADOW

export default theme
